import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';

const Chapitre3Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Déplacer les paires de tuyaux et les stocker dans une liste
        </p>
      </InfoBox>

      <Separator type="text" text="Liste de tuyaux" color="gray-darker" size="md" />
      <SectionTitle>🚀 1/ Création d'une liste pour afficher plusieurs paires de tuyaux</SectionTitle>

      <p>On aimerait pouvoir afficher plusieurs paires de tuyaux en même temps à l'écran, pour créer plusieurs obstacles à l'oiseau.</p>

      <p>Pour cela, on va créer une <strong>liste</strong> qui contiendra plusieurs <InlineCode>pairesTuyaux</InlineCode> :</p>

      <CodeBlock language="javascript">
{`let tuyaux = [];`}
      </CodeBlock>

      <p>Dans setup, on ajoute 2 tuyaux, un en x = 200 et un en x = 400 par exemple :</p>

      <CodeBlock language="javascript">
{`tuyaux.push({ x: 200, y: 100, franchi: false });
tuyaux.push({ x: 400, y: 100, franchi: false });`}
      </CodeBlock>

      <Note type="info" title="📚 Rappel">

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>liste.push(élément)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Ajoute un élément à notre liste.</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>lenght</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Nombre d'éléments dans notre liste.</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>
      </Note>

      <p>Et dans draw, on parcourt la liste des tuyaux pour tous les afficher un par un.</p>

      <Note type="warning" title="⚠️ Parcours inversé">
        <p>Attention : au lieu de parcourir la liste de 0 à <InlineCode>tuyaux.length - 1</InlineCode>, on va la parcourir à l'envers.<br/>Je t'expliquerai plus tard pourquoi c'est important, quand on supprimera les tuyaux qui sortent de l'écran.</p>
      </Note>

      <CodeBlock language="javascript">
{`// Afficher les tuyaux
for (let i = tuyaux.length - 1; i >= 0; i--) {
  let t = tuyaux[i];
  afficherTuyaux(t);
}`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : affichage de la liste de tuyaux">
        <p>Crée une liste globale qui contiendra toutes nos paires de tuyaux :</p>
        <CodeBlock language="javascript">
{`let tuyaux = [];`}
        </CodeBlock>

        <p>Dans <InlineCode>setup()</InlineCode>, ajoute 2 tuyaux en x = 200 et x = 400 :</p>
        <CodeBlock language="javascript">
{`tuyaux.push({ x: 200, y: 100, franchi: false });
tuyaux.push({ x: 400, y: 100, franchi: false });`}
        </CodeBlock>

        <p>Dans <InlineCode>draw()</InlineCode>, parcours la liste de tuyaux à l'envers, récupere le tuyau courant dans la variable <InlineCode>t</InlineCode> et affiche le tuyaux avec la fonction <InlineCode>afficherTuyaux(t)</InlineCode> :</p>
        <CodeBlock language="javascript">
{`for (let i = tuyaux.length - 1; i >= 0; i--) {
  let t = tuyaux[i];
  afficherTuyaux(t);
}`}
        </CodeBlock>
      <br/>
        <Result>Tu devrais voir 2 paires de tuyaux en x = 200 et x = 400.</Result>
   

      <CorrectionBox title="Correction :">
        <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyaux = [];

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
  tuyaux.push({ x: 200, y: 100, franchi: false });
  tuyaux.push({ x: 400, y: 100, franchi: false });
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);

  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];
    afficherTuyaux(t);
  }
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  // Rien ici pour l'instant
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}`}
        </CodeBlock>
      </CorrectionBox>

      </Note>
      

      <Separator type="text" text="Déplacement des tuyaux" color="gray-darker" size="md" />
      <SectionTitle>🏃‍♂️ 2/ Déplacement des tuyaux</SectionTitle>

      <p>Dans Flappy Bird, contrairement à ce qu'on pourrait penser, ce n'est pas l'oiseau qui avance, mais les tuyaux qui reculent !</p>

      <Note type="tip" title="💡 Astuce de gameplay">
        <p>En fait, l'oiseau ne bouge pas, et on recule les tuyaux pour donner l'illusion qu'on avance.</p>
      </Note>

      <p>On va donc déplacer les tuyaux vers la gauche.<br/>
      Il suffit de réduire la position x au fil du temps :</p>

      <CodeBlock language="javascript">
{`t.x = t.x - 2;`}
      </CodeBlock>

      <p><strong>Explication :</strong> à chaque frame, la paire de tuyaux stockée dans la variable <InlineCode>t</InlineCode> recule de 2 pixels.</p>

      <Note type="exercice" title="🧪 À toi de jouer : déplacer les tuyaux vers la gauche">
        <p>Dans draw, ajoute cette ligne juste avant l'appel à la fonction <InlineCode>afficherTuyaux(t)</InlineCode> :</p>
        <CodeBlock language="javascript">
{`t.x = t.x - 2;`}
        </CodeBlock>
        <br/>
        <Result>Les tuyaux reculent !</Result>
      

      <CorrectionBox title="Correction :">
        <CodeBlock language="javascript">
{`for (let i = tuyaux.length - 1; i >= 0; i--) {
  let t = tuyaux[i];
  t.x = t.x - 2;
  afficherTuyaux(t);
}`}
        </CodeBlock>
      </CorrectionBox>

      </Note>

      <Separator type="text" text="Vitesse indépendante" color="gray-darker" size="md" />
      <SectionTitle>⚡ 3/ Déplacement avec une vitesse indépendante du framerate</SectionTitle>

      <p>Le problème de retirer 2 pixels à chaque frame, c'est que si ton ordinateur tourne à 60 fps ou à 200 fps, la vitesse sera différente.<br/>

      On veut une vitesse constante, peu importe les performances.</p>

      <p>Pour cela, on utilise la variable spéciale de p5.js :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Variable</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>deltaTime</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Durée écoulée depuis le dernier draw, en millisecondes.</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="info" title="">
        <p>En haut du draw, on va créer une variable dt qui contiendra le temps écoulé depuis le dernier draw, en secondes (pas en millisecondes), car c'est plus simple de résonner en secondes :</p>
        <CodeBlock language="javascript">
{`let dt = deltaTime / 1000;`}
        </CodeBlock>

        <p>On remplacera la ligne :</p>
        <CodeBlock language="javascript">
{`t.x = t.x - 2;`}
        </CodeBlock>

        <p>par :</p>
        <CodeBlock language="javascript">
{`t.x = t.x - 2 * dt;`}
        </CodeBlock>

        <p>Afin de pouvoir équilibrer facilement le jeu dans le dernier chapitre, on va créer une variable globale <InlineCode>tuyauVitesse</InlineCode> qui détermine la vitesse de déplacement des tuyaux :</p>
        <CodeBlock language="javascript">
{`let tuyauVitesse = 150; // en pixels par seconde`}
        </CodeBlock>

        <p>Puis on utilsera <InlineCode>tuyauVitesse</InlineCode> a la place du <InlineCode>2</InlineCode> :</p>
        <CodeBlock language="javascript">
{`t.x = t.x - tuyauVitesse * dt;`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : déplacer les tuyaux indépendamment du framerate">
        <p>Crée une variable globale <InlineCode>tuyauVitesse</InlineCode> qui controlera la vitesse de déplacement des tuyaux :</p>
        <CodeBlock language="javascript">
{`let tuyauVitesse = 150;`}
        </CodeBlock>

        <p>Dans draw, crée une variable <InlineCode>dt</InlineCode> qui contiendra le temps écoulé depuis le dernier draw, en secondes, car on souhaite une vitesse en secondes :</p>
        <CodeBlock language="javascript">
{`let dt = deltaTime / 1000;`}
        </CodeBlock>

        <p>Modifie la ligne qui génére le déplacement des tuyaux par :</p>
        <CodeBlock language="javascript">
{`t.x = t.x - tuyauVitesse * dt;`}
        </CodeBlock>
      <br/>
        <Result>Visuellement, rien ne change. Mais maintenant, le déplacement des tuyaux est controlé par la variable <InlineCode>tuyauVitesse</InlineCode> et indépendant du framerate.</Result>

        <CorrectionBox title="Correction :">
        <CodeBlock language="javascript">
{`let tuyauVitesse = 150; // en globale, tout en haut du code

let dt = deltaTime / 1000;
for (let i = tuyaux.length - 1; i >= 0; i--) {
  let t = tuyaux[i];
  t.x = t.x - tuyauVitesse * dt;
  afficherTuyaux(t);
}`}
        </CodeBlock>
      </CorrectionBox>
      </Note>


      <Separator type="text" text="Exploration" color="gray-darker" size="md" />
      <SectionTitle>🔍 Pour aller plus loin</SectionTitle>

      <p>Teste différentes valeurs pour <InlineCode>tuyauVitesse</InlineCode> pour modifier la vitesse de déplacement des tuyaux :</p>
      <ul className="list-disc list-inside mb-4">
        <li><InlineCode>tuyauVitesse = 100</InlineCode></li>
        <li><InlineCode>tuyauVitesse = 200</InlineCode></li>
        <li><InlineCode>tuyauVitesse = 500</InlineCode></li>
      </ul>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Déplacement des tuyaux stockées dans une liste</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyaux = [];

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
  tuyaux.push({ x: 200, y: 100, franchi: false });
  tuyaux.push({ x: 400, y: 100, franchi: false });
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);
  }
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  // Rien ici pour l'instant
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}`}
      </CodeBlock>
    </>
  );
};

export default Chapitre3Content; 